import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/admin/metrics`;

/**
 * Names mirror BlackBox.Application.Manage.EngineSeries exactly, because the server parses and returns
 * them as written. Which of these can be split or aggregated how is NOT here — the catalogue endpoint
 * answers that, so the options a picker offers cannot drift from the tags the engine really records.
 */
export type EngineSeriesName =
  | "Transactions"
  | "TransactionCompute"
  | "TransactionRequestCharge"
  | "VendorLatency"
  | "PhaseDuration"
  | "PayloadBytes"
  | "LaneSends"
  | "LaneWait"
  | "LaneLongWaits"
  | "LanePauses"
  | "ChunksActive"
  | "LaneInflight"
  | "LaneBatchGap"
  | "RunQueueWait"
  | "PlatformRate"
  | "PlatformThrottles"
  | "ChunkFaults"
  | "DeadLetters"
  | "DeliveryBrokerLatency"
  | "DeliveryAttempts"
  | "DeliveryHostUptime"
  | "DeliveryPreamble"
  | "StoreOperations"
  | "StoreRequestCharge"
  | "StoreBytes"
  | "StoreBatchOperations"
  | "StoreSaveBatches"
  | "BrokerSends"
  | "CacheReads"
  | "HostCpu"
  | "HostMemory"
  | "HostThreadPoolQueue";

export type AggregationName =
  | "Rate"
  | "Total"
  /** How many distinct values of the split were there. Consumes its split, so it returns one unlabelled line. */
  | "Count"
  | "Average"
  | "Max"
  | "P50"
  | "P95"
  | "P99";

export type SplitName =
  | "None"
  | "Instance"
  | "Org"
  | "ServiceKind"
  | "TransactionOutcome"
  | "Phase"
  | "LaneClass"
  | "ThrottleReason"
  | "ThrottledDependency"
  | "DeadLetterReason"
  | "PayloadDirection"
  | "MessagingEntity"
  | "Store"
  | "StoreContainer"
  | "StoreOperation"
  | "Cache"
  | "CacheHit";

export type SeriesKindName = "Counter" | "Histogram" | "Gauge";

export interface MetricPoint {
  at: string;
  value: number;
}

/** One line on a chart. `label` is the split's value — empty for an unsplit query, which returns one line. */
export interface MetricLine {
  label: string;
  points: MetricPoint[];
}

export interface EngineMetricsResponse {
  series: EngineSeriesName;
  aggregation: AggregationName;
  splitBy: SplitName;
  /** What the values are in, so an axis can be labelled without a second table here. */
  unit: string;
  lines: MetricLine[];
}

/** One drawable series and what may be asked of it. The server owns this; a picker reads it. */
export interface SeriesDescription {
  series: EngineSeriesName;
  kind: SeriesKindName;
  splits: SplitName[];
  aggregations: AggregationName[];
}

export interface EngineSeriesRequest {
  series: EngineSeriesName;
  aggregation: AggregationName;
  /** Chart resolution in seconds. The server caps the resulting point count. */
  stepSeconds: number;
  /** How far back from now. Paired with the step, this is what decides the point count. */
  minutesBack: number;
  splitBy?: SplitName;
  /** Superadmin only; anyone else reads their own org whatever this says. */
  orgId?: string;
  /** One host. Taken as given — a host id names infrastructure, not a tenant. */
  instanceId?: string;
}

const EMPTY: MetricLine[] = [];

/**
 * Everything drawable, with the splits and aggregations each one allows. Effectively static — it changes
 * only when the engine gains an instrument — so it is cached hard rather than refetched with the panels.
 */
export function useEngineSeriesCatalogue() {
  const { data, isFetching, error } = useQuery({
    queryKey: ["admin-metrics-catalogue"],
    queryFn: () => fetchWrapper.get<SeriesDescription[]>(`${BASE}/catalogue`),
    staleTime: Infinity,
  });

  return {
    catalogue: computed(() => data.value ?? []),
    catalogueLoading: isFetching,
    catalogueError: error as Ref<ApiError | null>,
  };
}

/**
 * One series over a window, as chart lines. Superadmin only — the endpoint group carries the policy.
 *
 * The window ends at "now", which is rounded down to the step so the query key stops changing on every
 * tick: an unrounded end is a new key each render, which is a refetch loop rather than a live chart.
 */
export function useEngineSeries(request: Ref<EngineSeriesRequest>, enabled?: Ref<boolean>) {
  const url = computed(() => {
    const { series, aggregation, stepSeconds, minutesBack, splitBy, orgId, instanceId } = request.value;

    const stepMs = stepSeconds * 1000;
    const to = new Date(Math.floor(Date.now() / stepMs) * stepMs);
    const from = new Date(to.getTime() - minutesBack * 60_000);

    const params = new URLSearchParams({
      series,
      aggregation,
      from: from.toISOString(),
      to: to.toISOString(),
      step: String(stepSeconds),
    });
    if (splitBy && splitBy !== "None") params.set("splitBy", splitBy);
    if (orgId) params.set("orgId", orgId);
    if (instanceId) params.set("instanceId", instanceId);

    return `${BASE}?${params.toString()}`;
  });

  const { data, isFetching, error } = useQuery({
    queryKey: computed(() => ["admin-metrics", url.value]),
    queryFn: () => fetchWrapper.get<EngineMetricsResponse>(url.value),
    enabled,
  });

  return {
    lines: computed(() => data.value?.lines ?? EMPTY),
    unit: computed(() => data.value?.unit ?? ""),
    seriesLoading: isFetching,
    seriesError: error as Ref<ApiError | null>,
  };
}

/** Window choices for the engine panels, as a step that keeps every chart near a few hundred points. */
export const ENGINE_WINDOW_OPTIONS = [
  { label: "Last hour", minutesBack: 60, stepSeconds: 60 },
  { label: "Last 6 hours", minutesBack: 360, stepSeconds: 300 },
  { label: "Last 24 hours", minutesBack: 1440, stepSeconds: 900 },
  { label: "Last 7 days", minutesBack: 10080, stepSeconds: 3600 },
];
